function required(key: string, value: string | undefined): string {
  if (!value) throw new Error(`Missing ${key}. Copy web/.env.example and fill it in.`);
  return value;
}

/**
 * `false`, `0` and `off` are all things someone writes in an .env file meaning
 * off, and which one they reached for should not decide whether a feature ships.
 */
function envFlag(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined || value.trim() === '') return fallback;
  return !['false', '0', 'off', 'no'].includes(value.trim().toLowerCase());
}

/**
 * The Zendesk Web Widget key. Public either way — it ships in the page source of
 * any site running the widget — so it belongs in the committed .env.production
 * with the rest of the deployed configuration.
 */
const zendeskWidgetKey = import.meta.env.VITE_ZENDESK_WIDGET_KEY ?? '';

/**
 * Replaces Amplify's generated `aws-exports.js`.
 *
 * That file was gitignored and produced by `amplify pull`, so the deployed
 * configuration existed only on whichever machine last ran the CLI. These values
 * come from the CDK stack outputs instead — see the `WebEnvFile` output on
 * X5PortalFrontend.
 */
export const config = {
  apiUrl: required('VITE_API_URL', import.meta.env.VITE_API_URL),
  userPoolId: required('VITE_COGNITO_USER_POOL_ID', import.meta.env.VITE_COGNITO_USER_POOL_ID),
  userPoolClientId: required('VITE_COGNITO_CLIENT_ID', import.meta.env.VITE_COGNITO_CLIENT_ID),
  region: import.meta.env.VITE_AWS_REGION ?? 'us-west-2',
  zendeskWidgetKey,
  /**
   * The on/off switch for support chat: false means the snippet is never
   * injected, so the portal makes no request to Zendesk at all and no Support
   * control appears anywhere.
   *
   * Two inputs because they answer different questions. The key is credentials —
   * an environment that has never been given one cannot run chat regardless of
   * intent, which is why neither is `required()`: an absent key is a supported
   * state rather than a broken build. `VITE_SUPPORT_CHAT_ENABLED` is the policy
   * switch on top, so chat can be turned off for an environment that still holds
   * a working key, and turned back on without going to find the key again.
   * Absent means enabled, so an env file written before this flag existed keeps
   * behaving the way it did.
   */
  supportChatEnabled:
    zendeskWidgetKey !== '' && envFlag(import.meta.env.VITE_SUPPORT_CHAT_ENABLED, true),
} as const;
